import { useMemo } from 'react';
import { AlertTriangle, AlertCircle, Info, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  analyzeEmailDeliverability,
  getDeliverabilityScore,
  type DeliverabilityWarning,
  type WarningSeverity,
} from '@/lib/email-deliverability';

interface DeliverabilityPanelProps {
  html: string;
  subject: string;
}

const severityConfig: Record<
  WarningSeverity,
  { icon: typeof AlertTriangle; color: string; bg: string; label: string }
> = {
  high: {
    icon: AlertTriangle,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
    label: 'High Risk',
  },
  medium: {
    icon: AlertCircle,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50 border-yellow-200',
    label: 'Medium Risk',
  },
  low: {
    icon: Info,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
    label: 'Advisory',
  },
};

function ScoreGauge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600';
  const bgColor = score >= 80 ? 'bg-green-100' : score >= 60 ? 'bg-yellow-100' : 'bg-red-100';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Needs Work' : 'Poor';

  return (
    <div className="flex items-center gap-4">
      <div className={`flex h-16 w-16 items-center justify-center rounded-full ${bgColor}`}>
        <span className={`text-2xl font-bold ${color}`}>{score}</span>
      </div>
      <div>
        <p className={`text-lg font-semibold ${color}`}>{label}</p>
        <p className="text-muted-foreground text-sm">Deliverability Score</p>
      </div>
    </div>
  );
}

function WarningCard({ warning }: { warning: DeliverabilityWarning }) {
  const config = severityConfig[warning.severity];
  const Icon = config.icon;

  return (
    <div className={`rounded-lg border p-3 ${config.bg}`}>
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${config.color}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">{warning.title}</p>
            <Badge variant="outline" className="text-xs">
              {warning.category}
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">{warning.details}</p>
        </div>
      </div>
    </div>
  );
}

export default function DeliverabilityPanel({ html, subject }: DeliverabilityPanelProps) {
  const { warnings, score } = useMemo(() => {
    if (!html && !subject) return { warnings: [], score: 100 };
    const w = analyzeEmailDeliverability(html || '', subject || '');
    return { warnings: w, score: getDeliverabilityScore(w) };
  }, [html, subject]);

  const highWarnings = warnings.filter((w) => w.severity === 'high');
  const mediumWarnings = warnings.filter((w) => w.severity === 'medium');
  const lowWarnings = warnings.filter((w) => w.severity === 'low');

  return (
    <div className="space-y-4">
      {/* Score */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <ScoreGauge score={score} />
            {warnings.length === 0 ? (
              <div className="flex items-center gap-2 text-green-600">
                <ShieldCheck className="h-5 w-5" />
                <span className="text-sm font-medium">No issues found</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-yellow-600">
                <ShieldAlert className="h-5 w-5" />
                <span className="text-sm font-medium">
                  {warnings.length} issue{warnings.length !== 1 ? 's' : ''} found
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* High risk warnings */}
      {highWarnings.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4" />
              High Risk ({highWarnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {highWarnings.map((w) => (
              <WarningCard key={w.id} warning={w} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Medium risk warnings */}
      {mediumWarnings.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-yellow-600">
              <AlertCircle className="h-4 w-4" />
              Medium Risk ({mediumWarnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {mediumWarnings.map((w) => (
              <WarningCard key={w.id} warning={w} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Low risk / advisory */}
      {lowWarnings.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-blue-600">
              <Info className="h-4 w-4" />
              Advisory ({lowWarnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {lowWarnings.map((w) => (
              <WarningCard key={w.id} warning={w} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tips card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Best Practices</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-muted-foreground space-y-1.5 text-xs">
            <li>&#8226; Always include an unsubscribe link (legally required)</li>
            <li>&#8226; Keep subject lines under 60 characters</li>
            <li>&#8226; Maintain a healthy text-to-image ratio</li>
            <li>&#8226; Use full URLs instead of URL shorteners</li>
            <li>&#8226; Avoid spam trigger words like "free", "act now", "buy now"</li>
            <li>&#8226; Add alt text to all images</li>
            <li>&#8226; Keep HTML size under 100KB (Gmail clips at 102KB)</li>
            <li>&#8226; Test across email clients before sending</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
